import React, { useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  sx, Shell, Notice, BASE_ERROR_COPY, normalizeReportData, deepSet,
  fmtWhen, fmtDate, ReportHeader, ReportBodySections, BRAND_LINE,
  reportIssues, INCOMPLETE_REPORT_MSG, UnlinkedWorkersPanel, linkWorkerInForm, likelyUnlinkedNames,
} from './reportShared'
import { etToday } from '../lib/dateUtils'

// Public edit page (no login — the JWT in ?token= is the capability).
// Roles: 'foreman' (fills in / revises until the PM approves) and 'admin'
// (admin UI — can always edit and choose Save / Save and approve). Approval
// for everyone else happens in the authenticated approve view, not here.

const ERROR_COPY = {
  ...BASE_ERROR_COPY,
  token_expired: 'This edit link has expired. Please ask for the report to be re-sent.',
  token_not_authorized_for_edit: 'This link is not an edit link.',
  edit_window_closed: 'Your edit window has closed. Contact your PM for changes.',
  already_approved: 'This report has already been approved and can no longer be edited.',
  missing_report_data: 'There was nothing to save — please try again.',
  incomplete_report: INCOMPLETE_REPORT_MSG,
}

export default function ReportEditPage() {
  const [params] = useSearchParams()
  const token = params.get('token')

  const [phase, setPhase] = useState('loading') // loading | ready | error | blocked
  const [errorCode, setErrorCode] = useState(null)
  // { icon, title, msg, appUser } — appUser rides on the object because `role`
  // is NOT always set when we block (the load-time already-approved path returns
  // before setRole). true = admin/owner/pm token (has an app login to go back to).
  const [blocked, setBlocked] = useState(null)
  const [report, setReport] = useState(null)
  const [role, setRole] = useState(null)
  const [form, setForm] = useState(null)
  const [costCodes, setCostCodes] = useState([])
  const [scopeItems, setScopeItems] = useState([])   // PlanSwift task -> cost code picker
  const [employees, setEmployees] = useState([])
  const [fillIn, setFillIn] = useState(false) // freshly-published, not yet submitted
  const [submitted, setSubmitted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [saveError, setSaveError] = useState(null)
  const [validate, setValidate] = useState(false)   // show required-field highlights
  // Unlinked-worker hard stop (PM finalize / admin "Save and approve" only).
  const [unlinked, setUnlinked] = useState(null)
  const [linkRoster, setLinkRoster] = useState([])
  const [linkNote, setLinkNote] = useState(null)
  // true = the PM finalize / admin "Save and approve" SAVED the report and HELD
  // the approval (amber); false = an approval was BLOCKED (red, older server).
  const [unlinkedHeld, setUnlinkedHeld] = useState(false)

  // Admin = owner or admin (token role). Keep in sync with isAdminRole()
  // in api/lib/admin.js; CRA cannot import from api/.
  const isAdmin = role === 'admin' || role === 'owner'
  // PM one-step finalize token (the no-foreman fallback form email).
  const isPm = role === 'pm'

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!token) { setErrorCode('missing_token'); setPhase('error'); return }
      try {
        const r = await fetch(`/api/report-actions?action=verify&token=${encodeURIComponent(token)}`)
        const data = await r.json()
        if (cancelled) return
        if (!r.ok || !data.valid) { setErrorCode(data.error || 'invalid_token'); setPhase('error'); return }

        const admin = data.role === 'admin' || data.role === 'owner'

        // Inbound-first model: no edit window. The only hard block is an already
        // approved report — anyone with a valid link may edit until then.
        if (!admin && data.report.pm_approval_status === 'approved') {
          setBlocked({ icon: '✅', title: 'Report approved', msg: 'This report has already been approved and can no longer be edited.', appUser: admin || data.role === 'pm' })
          setPhase('blocked'); return
        }

        setReport(data.report)
        setRole(data.role)
        setForm(normalizeReportData(data.report.report_data))
        setCostCodes(Array.isArray(data.cost_codes) ? data.cost_codes : [])
        setScopeItems(Array.isArray(data.scope_items) ? data.scope_items : [])
        setEmployees(Array.isArray(data.employees) ? data.employees : [])
        // "Fill-in" mode: a freshly-published report the foreman hasn't submitted yet.
        setFillIn(!admin && data.report.pm_approval_status == null && !data.report.foreman_submitted_at)
        setPhase('ready')
      } catch (e) {
        if (!cancelled) { setErrorCode('db_error'); setPhase('error') }
      }
    }
    load()
    return () => { cancelled = true }
  }, [token])

  const set = useCallback((path, value) => {
    setForm(prev => deepSet(prev, path, value))
    setSavedAt(null) // edited since last save
  }, [])

  const removeAttendance = useCallback((idx) => {
    setForm(prev => ({ ...prev, attendance: (prev.attendance || []).filter((_, i) => i !== idx) }))
    setSavedAt(null)
  }, [])

  async function handleSave(approve = false) {
    setSaveError(null)
    // Hard block: a foreman submission (or an admin "Save and approve") must be
    // complete — every hours>0 row needs a cost code and every placeholder a name.
    if (!isAdmin || approve) {
      const issues = reportIssues(form)
      if (!issues.ok) { setValidate(true); setSaveError(INCOMPLETE_REPORT_MSG); return }
    }
    setSaving(true)
    try {
      const r = await fetch('/api/report-actions?action=save-foreman-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, report_data: form, approve }),
      })
      const data = await r.json()
      if (!r.ok || !data.ok) {
        if (data.error === 'report_deleted') {
          setBlocked({ icon: '🗑️', title: 'Report removed', msg: ERROR_COPY.report_deleted, appUser: isAdmin || isPm })
          setPhase('blocked'); return
        }
        if (data.error === 'edit_window_closed' || data.error === 'already_approved') {
          setBlocked(data.error === 'already_approved'
            ? { icon: '✅', title: 'Report approved', msg: ERROR_COPY.already_approved, appUser: isAdmin || isPm }
            : { icon: '🔒', title: 'Edit window closed', msg: ERROR_COPY.edit_window_closed, appUser: isAdmin || isPm })
          setPhase('blocked')
          return
        }
        if (data.error === 'incomplete_report') { setValidate(true); setSaveError(INCOMPLETE_REPORT_MSG); return }
        if (data.error === 'unlinked_workers') { adoptUnlinked(data, false); return }
        setSaveError(ERROR_COPY[data.error] || ERROR_COPY.db_error)
        return
      }
      if (data.held) {
        // SAVED (and, for the PM, SUBMITTED) — approval HELD. Not an error:
        // stay on the form with the link control; the next tap approves.
        adoptUnlinked(data, true)
        setSavedAt(data.saved_at || new Date().toISOString())
        if (data.submitted) setFillIn(false)
        return
      }
      setUnlinked(null); setLinkNote(null); setUnlinkedHeld(false)
      if (data.approved) {
        setBlocked({ icon: '✅', title: 'Report approved', msg: isPm
          ? 'Report submitted and finalized — it has been released to payroll. You are all set; no approval email will follow.'
          : 'The report was saved and approved.', appUser: isAdmin || isPm })
        setPhase('blocked')
        return
      }
      setSavedAt(data.saved_at || new Date().toISOString())
      // A foreman save IS a submission in the inbound-first flow. Surface it,
      // but keep the form editable so they can keep editing until PM approval.
      if (data.submitted) { setSubmitted(true); setFillIn(false) }
    } catch (e) {
      setSaveError(ERROR_COPY.db_error)
    } finally {
      setSaving(false)
    }
  }

  // Adopt the server's copy of report_data (exact-name auto-links applied, so the
  // row indexes in `unlinked` line up) and show the link control. `held` = the
  // edits were saved and the approval held (200); false = blocked (400).
  function adoptUnlinked(data, held) {
    if (data.report_data) setForm(normalizeReportData(data.report_data))
    setUnlinked(Array.isArray(data.unlinked) ? data.unlinked : [])
    setLinkRoster(Array.isArray(data.roster) ? data.roster : [])
    setLinkNote(null)
    setUnlinkedHeld(held)
  }

  function linkWorker(entry, emp) {
    const next = linkWorkerInForm(form, entry, emp)
    if (!next) { setSaveError('This report changed since it was checked — reload the page and try again.'); return }
    const remaining = (unlinked || []).filter(u => !(u.list === entry.list && u.index === entry.index))
    setForm(next)
    setSavedAt(null)
    setUnlinked(remaining)
    setLinkNote(remaining.length ? null : `All workers linked — tap ${isPm ? (unlinkedHeld ? 'Approve Report' : 'Submit & Finalize') : 'Save and approve'} again.`)
  }

  // This page is standalone chrome — no app header, no tabs — so a terminal
  // Notice needs its own way back into the app. Same deep link ReportApprovePage
  // uses: ?tab=field-reports is the one tab Dashboard whitelists for EVERY role.
  // Only offered to app users (admin/owner/pm tokens); a foreman token holder may
  // have no login at all, so the link would just wall him at /login.
  function backToFieldReports() {
    window.location.href = '/?tab=field-reports'
  }
  const backLinkStyle = {
    padding: 0, border: 'none', background: 'none', color: '#4a90d9',
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
  }

  if (phase === 'loading') {
    return <Notice icon="⏳" title="Loading report…" msg="One moment." />
  }
  if (phase === 'error') {
    return <Notice icon="⚠️" title="Can’t open this report" msg={ERROR_COPY[errorCode] || 'Something went wrong. Please try again.'} />
  }
  if (phase === 'blocked') {
    return (
      <Notice
        icon={blocked.icon}
        title={blocked.title}
        msg={blocked.msg}
        action={blocked.appUser
          ? <button onClick={backToFieldReports} style={backLinkStyle}>← Back to Field Reports</button>
          : <div style={{ fontSize: 13, color: '#999' }}>You can close this window.</div>}
      />
    )
  }

  // Pre-submit hint for the two submit/save-and-approve buttons: who the CLIENT
  // can already tell isn't linked (advisory — the server decides, and never
  // blocks the save). Hidden once the server's own list is on screen.
  const preUnlinked = (isPm || isAdmin) && !(unlinked || []).length ? likelyUnlinkedNames(form, employees) : []

  return (
    <Shell>
      <ReportHeader report={report} />

      <div style={sx.body}>
        {!!report?.report_date && report.report_date > etToday() && (
          <div style={sx.banner('#fff8f0', '#e8820c', '#92510a')}>
            ⚠️ This report’s Work Date (<strong>{fmtDate(report.report_date)}</strong>) is in the future. Make sure you’re reporting work for that day, not today.
          </div>
        )}
        <div style={sx.banner('#f0f7ff', '#4a90d9', '#1f5b8f')}>
          {isAdmin
            ? <>Make any corrections, then <strong>Save</strong> to keep the current status or <strong>Save and approve</strong> to finalize.</>
            : isPm
              ? <>No foreman got this form, so it's yours to complete as the PM. Fill in the <strong>actual hours and details</strong>, then tap <strong>Submit &amp; Finalize</strong> — your submission approves the report in one step. No approval email will follow.</>
              : fillIn
                ? <>Fill in the <strong>actual hours and details</strong> for today's work, then tap <strong>Submit Daily Report</strong> at the bottom. Cost codes are pre-filled — change them if needed.</>
                : <>Review the report and fix anything that’s wrong, then tap <strong>Submit Daily Report</strong> at the bottom. You can keep editing until your PM approves.</>}
        </div>
        {submitted ? (
          <div style={sx.banner('#eaf6ec', '#1e7e34', '#1e5128')}>
            ✅ <strong>Report submitted</strong>{savedAt ? ` ${fmtWhen(savedAt)}` : ''}. Your PM will review and approve. You can keep editing until then.
          </div>
        ) : savedAt && (
          <div style={sx.banner('#eaf6ec', '#1e7e34', '#1e5128')}>
            ✅ <strong>Changes saved</strong> {fmtWhen(savedAt)}.
          </div>
        )}

        <ReportBodySections report={report} form={form} ro={false} set={set} costCodes={costCodes}
          fillIn={fillIn} onDeleteAttendance={removeAttendance} employees={employees} validate={validate} scopeItems={scopeItems} />
      </div>

      <div style={sx.footer}>
        <UnlinkedWorkersPanel unlinked={unlinked} roster={linkRoster} busy={saving} note={linkNote} held={unlinkedHeld} onLink={linkWorker} />
        {saveError && (
          <div style={{ ...sx.banner('#fdecec', '#d9534f', '#a12622'), marginTop: 0 }}>{saveError}</div>
        )}
        {isAdmin ? (
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              style={{ ...sx.actionBtn(saving, '#4a90d9', '#a9c7e6'), flex: 1 }}
              disabled={saving}
              onClick={() => handleSave(false)}
            >
              {saving ? 'Saving…' : 'Save (still pending)'}
            </button>
            <button
              style={{ ...sx.actionBtn(saving, '#1e7e34', '#9bbfa6'), flex: 1 }}
              disabled={saving}
              onClick={() => handleSave(true)}
            >
              {saving ? 'Saving…' : 'Save and approve'}
            </button>
          </div>
        ) : (
          <button
            style={sx.actionBtn(saving, '#1e7e34', '#9bbfa6')}
            disabled={saving}
            onClick={() => handleSave(false)}
          >
            {saving ? 'Submitting…' : isPm ? (unlinkedHeld ? 'Approve Report' : 'Submit & Finalize') : submitted ? 'Submit again' : 'Submit Daily Report'}
          </button>
        )}
        <div style={{ fontSize: 12, color: '#888', textAlign: 'center', marginTop: 10 }}>
          {isAdmin
            ? (preUnlinked.length
              ? `${preUnlinked.join(', ')} ${preUnlinked.length === 1 ? 'isn’t' : 'aren’t'} in payroll yet - Save and approve will save your edits but approval waits until they’re linked.`
              : 'Your edits are recorded in the report’s audit trail under your name.')
            : isPm
              ? (preUnlinked.length
                ? `${preUnlinked.join(', ')} ${preUnlinked.length === 1 ? 'isn’t' : 'aren’t'} in payroll yet - this will submit the report but approval waits until they’re linked.`
                : 'Submitting finalizes the report in one step — no approval email will follow.')
              : 'Once you submit, your PM will review and approve. You can keep editing until then.'}
        </div>
      </div>

      <div style={sx.brand}>{BRAND_LINE}</div>
    </Shell>
  )
}
